import { ToggleAdminMode } from "@/components/admin/toggle-admin-mode";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useAdminMode } from "@/contexts/admin-mode-provider";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import {
  CompanyDTO,
  CompanyDatasourceStatus,
  CompanySourcesEditDTO,
} from "@shaarp/common";
import { Loader2Icon, LockIcon, LockOpenIcon, RefreshCwIcon } from "lucide-react";
import { useState } from "react";
import { BiSolidBadgeCheck } from "react-icons/bi";
import { RiExternalLinkLine, RiGoogleFill } from "react-icons/ri";
import { Link } from "react-router-dom";

export default function DialogUpdateCompanySources({
  company,
  onSaved = () => {},
  onClose = () => {},
}: {
  company: CompanyDTO;
  onSaved?: () => void;
  onClose?: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();
  const toastError = useToastError();
  const { t } = useTranslation();
  const { isAdminMode } = useAdminMode();

  const [dialogVerified, setDialogVerified] = useState(false);
  const [dialogUnVerified, setDialogUnVerified] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);

  const [companyName, setCompanyName] = useState<string>(company.name || "");
  const [isNameLocked, setIsNameLocked] = useState<boolean>(
    company.isNameLocked
  );

  const [siretStatus, setSiretStatus] = useState<CompanyDatasourceStatus>(
    company.dataSources.siretStatus
  );
  const [siret, setSiret] = useState<string | null>(company.business.siret);
  const [googleMapsStatus, setGoogleMapsStatus] =
    useState<CompanyDatasourceStatus>(company.dataSources.googleMapsStatus);
  const [googleMapsUrl, setGoogleMapsUrl] = useState<string | null>(
    company.links.googleMaps
  );
  const [websiteStatus, setWebsiteStatus] = useState<CompanyDatasourceStatus>(
    company.dataSources.websiteStatus
  );
  const [website, setWebsite] = useState<string | null>(company.website);
  const [linkedinStatus, setLinkedinStatus] = useState<CompanyDatasourceStatus>(
    company.dataSources.linkedinStatus
  );
  const [linkedinUrl, setLinkedinUrl] = useState<string | null>(
    company.socialNetworks.linkedin.url
  );

  const [instagramStatus, setInstagramStatus] =
    useState<CompanyDatasourceStatus>(company.dataSources.instagramStatus);
  const [instagramUrl, setInstagramUrl] = useState<string | null>(
    company.socialNetworks.instagram.url
  );
  const [facebookStatus, setFacebookStatus] = useState<CompanyDatasourceStatus>(
    company.dataSources.facebookStatus
  );
  const [facebookUrl, setFacebookUrl] = useState<string | null>(
    company.socialNetworks.facebook.url
  );
  const [xStatus, setXStatus] = useState<CompanyDatasourceStatus>(
    company.dataSources.xStatus
  );
  const [xUrl, setXUrl] = useState<string | null>(company.socialNetworks.x.url);
  const [youtubeStatus, setYoutubeStatus] = useState<CompanyDatasourceStatus>(
    company.dataSources.youtubeStatus
  );
  const [youtubeUrl, setYoutubeUrl] = useState<string | null>(
    company.socialNetworks.youtube.url
  );
  const [tiktokStatus, setTiktokStatus] = useState<CompanyDatasourceStatus>(
    company.dataSources.tiktokStatus
  );
  const [tiktokUrl, setTiktokUrl] = useState<string | null>(
    company.socialNetworks.tiktok.url
  );

  const sources = [
    {
      name: "siret",
      title: t("company.sourcesDialog.sources.siret.title"),
      placeholder: t("company.sourcesDialog.sources.siret.placeholder"),
      status: siretStatus,
      setStatus: setSiretStatus,
      data: siret,
      dataSetter: setSiret,
    },
    {
      name: "googlemaps",
      title: t("company.sourcesDialog.sources.googleMaps.title"),
      placeholder: t("company.sourcesDialog.sources.googleMaps.placeholder"),
      status: googleMapsStatus,
      setStatus: setGoogleMapsStatus,
      data: googleMapsUrl,
      dataSetter: setGoogleMapsUrl,
    },
    {
      name: "website",
      title: t("company.sourcesDialog.sources.website.title"),
      placeholder: t("company.sourcesDialog.sources.website.placeholder"),
      status: websiteStatus,
      setStatus: setWebsiteStatus,
      data: website,
      dataSetter: setWebsite,
    },
    {
      name: "linkedin",
      title: t("company.sourcesDialog.sources.linkedin.title"),
      placeholder: t("company.sourcesDialog.sources.linkedin.placeholder"),
      status: linkedinStatus,
      setStatus: setLinkedinStatus,
      data: linkedinUrl,
      dataSetter: setLinkedinUrl,
    },
    {
      name: "instagram",
      title: t("company.sourcesDialog.sources.instagram.title"),
      placeholder: t("company.sourcesDialog.sources.instagram.placeholder"),
      status: instagramStatus,
      setStatus: setInstagramStatus,
      data: instagramUrl,
      dataSetter: setInstagramUrl,
    },
    {
      name: "facebook",
      title: t("company.sourcesDialog.sources.facebook.title"),
      placeholder: t("company.sourcesDialog.sources.facebook.placeholder"),
      status: facebookStatus,
      setStatus: setFacebookStatus,
      data: facebookUrl,
      dataSetter: setFacebookUrl,
    },
    {
      name: "x",
      title: t("company.sourcesDialog.sources.x.title"),
      placeholder: t("company.sourcesDialog.sources.x.placeholder"),
      status: xStatus,
      setStatus: setXStatus,
      data: xUrl,
      dataSetter: setXUrl,
    },
    {
      name: "youtube",
      title: t("company.sourcesDialog.sources.youtube.title"),
      placeholder: t("company.sourcesDialog.sources.youtube.placeholder"),
      status: youtubeStatus,
      setStatus: setYoutubeStatus,
      data: youtubeUrl,
      dataSetter: setYoutubeUrl,
    },
    {
      name: "tiktok",
      title: t("company.sourcesDialog.sources.tiktok.title"),
      placeholder: t("company.sourcesDialog.sources.tiktok.placeholder"),
      status: tiktokStatus,
      setStatus: setTiktokStatus,
      data: tiktokUrl,
      dataSetter: setTiktokUrl,
    },
  ];

  const valuesHasData = [
    {
      value: "UNKNOWN",
      text: t("company.sourcesDialog.status.unknown"),
      adminOnly: false,
    },
    {
      value: "EXIST",
      text: t("company.sourcesDialog.status.exists"),
      adminOnly: false,
    },
    {
      value: "DISCOVERED",
      text: t("company.sourcesDialog.status.discovered"),
      adminOnly: true,
    },
    {
      value: "NOT_EXIST",
      text: t("company.sourcesDialog.status.notExists"),
      adminOnly: false,
    },
  ];

  const onSubmit = async () => {
    try {
      await api.post(`/company/${company.id}/sources`, {
        sources: {
          companyName: companyName || null,
          isNameLocked,
          siretStatus,
          siret: siretStatus !== "NOT_EXIST" ? siret : null,
          googleMapsStatus: googleMapsStatus,
          googleMapsUrl: googleMapsUrl || null,
          websiteStatus: websiteStatus,
          websiteUrl: website || null,
          linkedinStatus: linkedinStatus,
          linkedinUrl: linkedinUrl || null,
          instagramStatus: instagramStatus,
          instagramUrl: instagramUrl || null,
          facebookStatus: facebookStatus,
          facebookUrl: facebookUrl || null,
          xStatus: xStatus,
          xUrl: xUrl || null,
          youtubeStatus: youtubeStatus,
          youtubeUrl: youtubeUrl || null,
          tiktokStatus: tiktokStatus,
          tiktokUrl: tiktokUrl || null,
        } as CompanySourcesEditDTO,
      });
      toast({
        variant: "default",
        title: t("company.sourcesDialog.notifications.updateTitle"),
        description: t("company.sourcesDialog.notifications.updateSuccess"),
      });
      onSaved();
    } catch (error) {
      toastError();
    }
  };

  const onRefetch = async () => {
    setIsRefetching(true);
    try {
      await api.post(`/company/${company.id}/refetch`);
      toast({
        variant: "default",
        title: t("company.sourcesDialog.notifications.refetchTitle"),
        description: t("company.sourcesDialog.notifications.refetchSuccess"),
      });
      onSaved();
    } catch {
      toastError();
    } finally {
      setIsRefetching(false);
    }
  };

  const setVerified = async (newVerifiedState: boolean) => {
    try {
      await api.post(`/company/${company.id}/sources/verified`, {
        verified: newVerifiedState,
      });

      if (newVerifiedState) {
        toast({
          variant: "default",
          title: t("company.sourcesDialog.notifications.verifiedTitle"),
          description: t("company.sourcesDialog.notifications.verifiedSuccess"),
        });
      } else {
        toast({
          variant: "default",
          title: t("company.sourcesDialog.notifications.unverifiedTitle"),
          description: t(
            "company.sourcesDialog.notifications.unverifiedSuccess"
          ),
        });
      }

      onSaved();
    } catch (error) {
      toastError();
    }
  };

  return (
    <>
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-[800px] w-full">
          <DialogHeader>
            <DialogTitle>
              {t("company.sourcesDialog.title", { companyName: company.name })}
            </DialogTitle>
          </DialogHeader>
          <Separator className="my-4" />
          <div className="overflow-auto max-h-[70vh] px-3">
            <div className="space-y-5">
              {isAdminMode && (
                <>
                  <div className="grid w-full items-center gap-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="companyName">
                        {t("company.sourcesDialog.companyName.title")}
                      </Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-2">
                            {isNameLocked ? (
                              <LockIcon className="size-4 text-warning" />
                            ) : (
                              <LockOpenIcon className="size-4 text-muted-foreground" />
                            )}
                            <Switch
                              id="isNameLocked"
                              checked={isNameLocked}
                              onCheckedChange={setIsNameLocked}
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          {isNameLocked
                            ? t(
                                "company.sourcesDialog.companyName.lockedTooltip"
                              )
                            : t(
                                "company.sourcesDialog.companyName.unlockedTooltip"
                              )}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <Input
                      type="text"
                      className="border-warning"
                      id="companyName"
                      placeholder={t(
                        "company.sourcesDialog.companyName.placeholder"
                      )}
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                    />
                  </div>
                  <Separator />
                </>
              )}
              {sources.map((source) => (
                <div
                  key={source.name}
                  className="grid w-full items-center gap-1.5"
                >
                  <div className="flex items-center gap-2">
                    <Label htmlFor={source.name}>{source.title}</Label>
                  </div>
                  {!!source.status && (
                    <RadioGroup
                      value={source.status as string}
                      onValueChange={(value) =>
                        source.setStatus(value as CompanyDatasourceStatus)
                      }
                      defaultValue="UNKNOWN"
                      className="flex flex-row gap-3"
                      disabled={
                        source.status === "DISCOVERED" && !isAdminMode
                      }
                    >
                      {valuesHasData
                        .filter(
                          (option) => !option.adminOnly || isAdminMode
                        )
                        .map((option) => (
                          <span
                            key={`${source.name}-${option.value}`}
                            className="flex items-center gap-1.5"
                          >
                            <RadioGroupItem
                              id={`${source.name}-${option.value}`}
                              value={option.value}
                            />
                            <Label
                              htmlFor={`${source.name}-${option.value}`}
                            >
                              {option.text}
                            </Label>
                          </span>
                        ))}
                    </RadioGroup>
                  )}
                  {source.status !== "NOT_EXIST" && (
                    <div className="flex items-center w-full space-x-2">
                      <Input
                        type="text"
                        id={source.name}
                        placeholder={source.placeholder}
                        value={source.data as string}
                        onChange={(e) => source.dataSetter(e.target.value)}
                        disabled={
                          source.status === "DISCOVERED" && !isAdminMode
                        }
                      />
                      {!!source.status && source.data && (
                        <Tooltip>
                          <TooltipTrigger>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              asChild
                            >
                              <Link
                                to={
                                  source.name === "siret"
                                    ? `https://www.pappers.fr/entreprise/${source.data}`
                                    : (source.data as string)
                                }
                                target="_blank"
                              >
                                <RiExternalLinkLine />
                              </Link>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {t("company.sourcesDialog.actions.openInNewTab")}
                          </TooltipContent>
                        </Tooltip>
                      )}
                      <Tooltip>
                        <TooltipTrigger>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            asChild
                          >
                            <Link
                              to={`https://www.google.com/search?q=${company.name.replaceAll(
                                " ",
                                "+"
                              )}+${source.name.replaceAll(" ", "+")}`}
                              target="_blank"
                            >
                              <RiGoogleFill />
                            </Link>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {t("company.sourcesDialog.actions.searchOnGoogle")}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <Separator className="my-4" />
          <DialogFooter>
            <ToggleAdminMode>
              <Button
                type="button"
                variant="secondary"
                onClick={onRefetch}
                disabled={isRefetching}
                className="mr-auto border-2 border-warning"
              >
                {isRefetching ? (
                  <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCwIcon className="w-4 h-4 mr-2" />
                )}
                {t("company.sourcesDialog.actions.refetch")}
              </Button>
            </ToggleAdminMode>
            <ToggleAdminMode>
              {company.isVerified ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setDialogUnVerified(true)}
                  className="mr-auto border-2 border-warning"
                >
                  <BiSolidBadgeCheck className="w-4 h-4 mr-2" />
                  {t("company.sourcesDialog.actions.markAsUnverified")}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setDialogVerified(true)}
                  className="mr-auto border-2 border-warning"
                >
                  <BiSolidBadgeCheck className="w-4 h-4 mr-2" />
                  {t("company.sourcesDialog.actions.markAsVerified")}
                </Button>
              )}
            </ToggleAdminMode>
            <DialogClose asChild>
              <Button variant="ghost">
                {t("company.sourcesDialog.actions.close")}
              </Button>
            </DialogClose>
            <Button onClick={() => onSubmit()}>
              {t("company.sourcesDialog.actions.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ToggleAdminMode>
        <AlertDialog open={dialogVerified} onOpenChange={setDialogVerified}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("company.sourcesDialog.verification.verifiedConfirmation")}
              </AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>
                {t("company.sourcesDialog.actions.cancel")}
              </AlertDialogCancel>
              <AlertDialogAction onClick={() => setVerified(true)}>
                {t("company.sourcesDialog.actions.confirm")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </ToggleAdminMode>

      <ToggleAdminMode>
        <AlertDialog open={dialogUnVerified} onOpenChange={setDialogUnVerified}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("company.sourcesDialog.verification.unverifiedConfirmation")}
              </AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>
                {t("company.sourcesDialog.actions.cancel")}
              </AlertDialogCancel>
              <AlertDialogAction onClick={() => setVerified(false)}>
                {t("company.sourcesDialog.actions.confirm")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </ToggleAdminMode>
    </>
  );
}
